import * as React from 'react';
import { useState } from 'react';
import Papa from 'papaparse';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Upload as UploadIcon, FileText as FileTextIcon, CheckCircle as CheckCircleIcon, Trash as TrashIcon, Pencil as PencilIcon, Download as DownloadIcon } from '@phosphor-icons/react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import axios from '@/lib/v2/axios';

import { useUser } from '@/hooks/v2/use-user';

interface ImportResult {
  success: boolean;
  message: string;
  details?: {
    unitsCreated: number;
    usersCreated: number;
    communitiesCreated: number;
    errors?: string[];
  };
}

export default function CommunitySettingsImport(): React.JSX.Element {
  const { community } = useUser();
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [editableData, setEditableData] = useState<any[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Helper function to parse unit string and display parsed information
  const parseUnitDisplay = (unitString: string) => {
    if (!unitString) return { unitNumber: '', address: '', city: '', state: '', zip: '' };
    
    const result = {
      unitNumber: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    };

    // Format: "UNIT 204 - ADDRESS 416 PORTOFINO DR # 204 SAN CARLOS CA 94070"
    if (unitString.includes('UNIT') && unitString.includes('- ADDRESS')) {
      const parts = unitString.split('- ADDRESS');
      if (parts.length === 2) {
        // Extract unit number
        const unitPart = parts[0].replace('UNIT', '').trim();
        result.unitNumber = unitPart;
        
        // Extract address part
        const addressPart = parts[1].trim();
        
        // Try to extract city, state, zip
        const addressMatch = /^(.*?)(?:\s+([A-Z\s]+)\s+([A-Z]{2})\s+(\d{5}))?$/i.exec(addressPart);
        if (addressMatch) {
          let streetAddress = addressMatch[1].trim();
          // Clean up street address
          streetAddress = streetAddress.replace(/\s+#\s*\d+$/, '');
          streetAddress = streetAddress.replace(/\s+UNIT\s+\d+$/, '');
          
          result.address = streetAddress;
          
          if (addressMatch[2]) result.city = addressMatch[2].trim();
          if (addressMatch[3]) result.state = addressMatch[3].toUpperCase();
          if (addressMatch[4]) result.zip = addressMatch[4];
        } else {
          result.address = addressPart;
        }
      }
    } else if (unitString.startsWith('Address')) {
      // Format: "Address 630 FOSTER COURT #1"
      const addressPart = unitString.replace('Address', '').trim();
      if (addressPart.includes('#')) {
        const [street, unit] = addressPart.split('#');
        result.address = street.trim();
        result.unitNumber = unit.trim();
      } else {
        result.address = addressPart;
      }
    } else {
      // Fallback
      result.address = unitString;
    }
    
    return result;
  };

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      validateFile(file);
    } else {
      toast.error('Please select a valid CSV or Excel file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const parseCSV = (file: File) => {
    // First, let's read the file as text to debug the raw content
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawText = e.target?.result as string;
      console.log('Raw CSV content (first 1000 chars):', rawText.substring(0, 1000));
      console.log('Raw CSV lines:', rawText.split('\n').slice(0, 5));
      
      // Clean up the CSV content - remove malformed first line if it exists
      let cleanText = rawText;
      const lines = rawText.split('\n');
      
      // Check if first line is malformed (not a proper CSV header)
      if (lines.length > 1 && lines[0].trim() === 'Table 1') {
        console.log('Removing malformed first line:', lines[0]);
        cleanText = lines.slice(1).join('\n');
        console.log('Cleaned CSV content (first 500 chars):', cleanText.substring(0, 500));
      }
      
      // Now parse with papaparse
      Papa.parse(cleanText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          console.log('Papa Parse Results:', results);
          console.log('Headers detected:', results.meta.fields);
          console.log('Data rows:', results.data.length);
          console.log('First data row:', results.data[0]);
          
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }
          
          // Check if headers were detected properly
          if (!results.meta.fields || results.meta.fields.length === 0) {
            console.error('No headers detected by papaparse');
            toast.error('Could not detect CSV headers. Please check the file format.');
            return;
          }
          
          // Map common CSV column variations to our standard names (matching ImportResidentsCommand)
          const columnMapping: Record<string, string> = {
            'Unit': 'Unit',
            'unit': 'Unit',
            'UNIT': 'Unit',
            'Unit #': 'Unit',
            'Unit Number': 'Unit',
            'Name': 'Name',
            'name': 'Name',
            'NAME': 'Name',
            'Full Name': 'Name',
            'Resident Name': 'Name',
            'Email': 'Email',
            'email': 'Email',
            'EMAIL': 'Email',
            'Email Address': 'Email',
            'Phone': 'Phone',
            'phone': 'Phone',
            'PHONE': 'Phone',
            'Phone Number': 'Phone',
            'Phone 2': 'Phone2',
            'phone2': 'Phone2',
            'Secondary Phone': 'Phone2',
            'Vehicle': 'Vehicle',
            'vehicle': 'Vehicle',
            'VEHICLE': 'Vehicle',
            'Delinquent': 'Delinquent',
            'delinquent': 'Delinquent',
            'DELINQUENT': 'Delinquent'
          };
          
          // Process the parsed data (matching ImportResidentsCommand approach)
          const processedData = results.data.map((row: any, index: number) => {
            const processedRow: any = {};
            
            // Map values to normalized column names
            results.meta.fields?.forEach((field: string) => {
              const normalizedField = columnMapping[field] || field;
              const value = row[field] || '';
              
              // Keep the original Unit value intact - let backend parse it
              if (normalizedField === 'Unit') {
                processedRow[normalizedField] = value;
              } else {
                processedRow[normalizedField] = value;
              }
            });
            
            // Keep original data for debugging
            results.meta.fields?.forEach((field: string) => {
              processedRow[`_original_${field}`] = row[field] || '';
            });
            
            console.log(`Processed row ${index + 1}:`, processedRow);
            return processedRow;
          });
          
          // Filter out completely empty rows
          const filteredData = processedData.filter(row => {
            const hasBasicInfo = (row.Name && row.Name.trim() !== '') || (row.Email && row.Email.trim() !== '');
            const hasUnit = row.Unit && row.Unit.trim() !== '';
            
            console.log(`Row filtering:`, row, `Has basic info: ${hasBasicInfo}, Has unit: ${hasUnit}`);
            
            const shouldInclude = hasBasicInfo || hasUnit;
            if (!shouldInclude) {
              console.log(`Excluding row:`, row, `- No meaningful data`);
            }
            
            return shouldInclude;
          });
          
          console.log('Final filtered data:', filteredData);
          console.log('Data length:', filteredData.length);
          
          // Store in both arrays
          setCsvData(filteredData);
          setEditableData([...filteredData]);
          
          console.log('csvData set:', filteredData.length, 'rows');
          console.log('editableData set:', filteredData.length, 'rows');
          
          setShowPreview(true);
        },
        error: (error: any) => {
          console.error('CSV parsing error:', error);
          toast.error('Error parsing CSV file. Please check the file format.');
        }
      });
    };
    reader.readAsText(file);
  };

  const validateFile = async (file: File) => {
    if (!community) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`/api/v2/communities/${community.id}/validate-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // File is valid, show preview
        setCsvData(response.data.data);
        setEditableData(response.data.data);
        setShowPreview(true);
        toast.success(`File validated successfully! ${response.data.summary.total_rows} records found.`);
      } else {
        // File has validation errors but still show data for editing
        setCsvData(response.data.data || []);
        setEditableData(response.data.data || []);
        setShowPreview(true);
        toast.warning(`File has validation errors but can still be edited. ${response.data.summary?.total_rows || 0} records found.`);
      }
    } catch (error: unknown) {
      console.error('File validation error:', error);
      const errorMessage = error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data ? String(error.response.data.message) : 'File validation failed. Please check the file format.';
      toast.error(errorMessage);
      
      // Try to parse locally as fallback
      if (file.type === 'text/csv') {
        parseCSV(file);
      }
    }
  };

  const handleImport = async () => {
    if (!editableData.length || !community) return;

    setIsImporting(true);
    setImportProgress(0);

    try {
      // Process the data to include parsed address fields
      const processedData = editableData.map(row => {
        const parsed = parseUnitDisplay(row.Unit || '');
        return {
          ...row,
          // Include parsed address fields if they exist
          unit_number: row.unit_number || parsed.unitNumber || '',
          address: row.address || parsed.address || '',
          city: row.city || parsed.city || '',
          state: row.state || parsed.state || '',
          zip_code: row.zip_code || parsed.zip || '',
        };
      });

      // Send the processed data instead of the file
      const response = await axios.put(`/api/v2/communities/${community.id}/import-residents`, {
        residents_data: processedData,
      }, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setImportProgress(progress);
          }
        },
      });

      setImportResult(response.data);
      toast.success('Import completed successfully!');
      
      // Reset form
      setSelectedFile(null);
      setCsvData([]);
      setShowPreview(false);
    } catch (error: unknown) {
      console.error('Import error:', error);
      const errorMessage = error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data ? String(error.response.data.message) : 'Import failed. Please try again.';
      toast.error(errorMessage);
      setImportResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setCsvData([]);
    setEditableData([]);
    setEditingRow(null);
    setShowPreview(false);
    setImportResult(null);
  };

  const handleEditRow = (index: number) => {
    setEditingRow(index);
  };

  const handleSaveRow = (index: number, updatedData: any) => {
    const newData = [...editableData];
    newData[index] = updatedData;
    setEditableData(newData);
    setEditingRow(null);
  };

  const handleRemoveRow = (index: number) => {
    const newData = editableData.filter((_, i) => i !== index);
    setEditableData(newData);
    setCsvData(newData);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
  };

  return (
    <Stack spacing={4}>
      <Card>
        <CardHeader
          action={
            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => window.open(`/api/v2/communities/download-template/csv`, '_blank')}
                size="small"
                startIcon={<DownloadIcon size={16} />}
                variant="outlined"
              >
                CSV Template
              </Button>
              <Button
                onClick={() => window.open(`/api/v2/communities/download-template/excel`, '_blank')}
                size="small"
                startIcon={<DownloadIcon size={16} />}
                variant="outlined"
              >
                Excel Template
              </Button>
            </Stack>
          }
          subheader="Upload a CSV or Excel file to import resident information into your community"
          title="Import Residents"
        />
        <CardContent>
          <Stack spacing={3}>
            {/* File Upload Area */}
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon color="var(--mui-palette-primary-main)" size={48} />
              <Typography sx={{ mt: 2, mb: 1 }} variant="h6">
                {isDragActive ? 'Drop the file here' : 'Drag & drop a CSV or Excel file here'}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                or click to select a file
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, display: 'block' }} variant="caption">
                Supported format: CSV or Excel files with columns: Unit, Name, Email, Phone, Phone 2, Vehicle, Delinquent
              </Typography>
            </Box>



            {/* File Info */}
            {selectedFile ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FileTextIcon size={24} />
                <Box>
                  <Typography sx={{ fontWeight: 500 }} variant="body2">
                    {selectedFile.name}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {(selectedFile.size / 1024).toFixed(1)} KB • {editableData.length} records found
                  </Typography>
                  {csvData.length > 0 && (
                    <Typography color="success.main" sx={{ display: 'block' }} variant="caption">
                      ✓ File validated by server
                    </Typography>
                  )}
                </Box>
                <Button color="secondary" onClick={resetImport} size="small">
                  Remove
                </Button>
              </Box> : null}
 {/* CSV Preview Section - Integrated into the same card */}
 {showPreview ? <>
                <Divider sx={{ my: 3 }} />
                
                {/* Data Summary */}
                <Box sx={{ mb: 3 }}>
                  <Typography gutterBottom variant="h6">
                    Data Summary
                  </Typography>
                  <Stack alignItems="center" direction="row" spacing={4} sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Total Records:</strong> {editableData.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Valid Records:</strong> {editableData.filter(row => row.Name && row.Email).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Records with Units:</strong> {editableData.filter(row => row.Unit).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Records with Alt Phone:</strong> {editableData.filter(row => row.Phone2 && row.Phone2.trim() !== '').length}
                    </Typography>
                  </Stack>
                  
                  {/* Address Parsing Summary */}
                 
                  
                  {/* Show detected columns */}
                  {csvData.length > 0 && (
                    <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 1 }}>
                      <Typography color="primary.contrastText" variant="body2">
                        <strong>Detected Columns:</strong> {Object.keys(csvData[0] || {}).filter(key => !key.startsWith('_original_')).join(', ')}
                      </Typography>
                      <Typography color="primary.contrastText" sx={{ mt: 1, display: 'block', opacity: 0.8 }} variant="caption">
                        Original CSV columns: {Object.keys(csvData[0] || {}).filter(key => key.startsWith('_original_')).map(key => key.replace('_original_', '')).join(', ')}
                      </Typography>
                      
                      {/* Debug: Show first few rows */}
                      {csvData.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                          <Typography color="primary.contrastText" sx={{ display: 'block', mb: 1 }} variant="caption">
                            <strong>First 3 rows:</strong>
                          </Typography>
                          {csvData.slice(0, 3).map((row: any, index: number) => {
                            const parsed = parseUnitDisplay(row.Unit || '');
                            return (
                              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 0.5 }}>
                                <Typography color="primary.contrastText" sx={{ display: 'block', fontWeight: 'bold' }} variant="caption">
                                  Row {index + 1}:
                                </Typography>
                                <Typography color="primary.contrastText" sx={{ display: 'block', opacity: 0.9 }} variant="caption">
                                  Unit: {parsed.unitNumber || 'N/A'} | Address: {parsed.address || 'N/A'}
                                </Typography>
                                <Typography color="primary.contrastText" sx={{ display: 'block', opacity: 0.8 }} variant="caption">
                                  Location: {[parsed.city, parsed.state, parsed.zip].filter(Boolean).join(', ') || 'N/A'}
                                </Typography>
                                <Typography color="primary.contrastText" sx={{ display: 'block', opacity: 0.7 }} variant="caption">
                                  Name: "{row.Name || 'N/A'}" | Email: "{row.Email || 'N/A'}"
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Preview Table */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Preview Table ({editableData.length} records)
                    </Typography>
                    <Button color="secondary" onClick={() => { setShowPreview(false); }} size="small">
                      Hide Preview
                    </Button>
                  </Box>
                  
                  <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', maxHeight: 500 }} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Unit #</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>ZIP</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Phone 2</TableCell>
                          <TableCell>Vehicle</TableCell>
                          <TableCell>Delinquent</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editableData.map((row: any, index: number) => {
                          const parsed = parseUnitDisplay(row.Unit || '');
                          return (
                            <TableRow hover key={index}>
                              {/* Unit Number */}
                              <TableCell>
                                {editingRow === index ? (
                                  <TextField
                                    fullWidth
                                    onChange={(e) => {
                                      const newData = [...editableData];
                                      newData[index] = { ...newData[index], unit_number: e.target.value };
                                      setEditableData(newData);
                                    }}
                                    size="small"
                                    value={row.unit_number || parsed.unitNumber || ''}
                                  />
                                ) : (
                                  <Chip 
                                    color="primary" 
                                    label={row.unit_number || parsed.unitNumber || 'N/A'} 
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              
                              {/* Address */}
                              <TableCell>
                                {editingRow === index ? (
                                  <TextField
                                    fullWidth
                                    onChange={(e) => {
                                      const newData = [...editableData];
                                      newData[index] = { ...newData[index], address: e.target.value };
                                      setEditableData(newData);
                                    }}
                                    size="small"
                                    value={row.address || parsed.address || ''}
                                  />
                                ) : (
                                  <Typography noWrap variant="body2">
                                    {row.address || parsed.address || 'N/A'}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* City */}
                              <TableCell>
                                {editingRow === index ? (
                                  <TextField
                                    fullWidth
                                    onChange={(e) => {
                                      const newData = [...editableData];
                                      newData[index] = { ...newData[index], city: e.target.value };
                                      setEditableData(newData);
                                    }}
                                    size="small"
                                    value={row.city || parsed.city || ''}
                                  />
                                ) : (
                                  <Typography variant="body2">
                                    {row.city || parsed.city || 'N/A'}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* State */}
                              <TableCell>
                                {editingRow === index ? (
                                  <TextField
                                    fullWidth
                                    onChange={(e) => {
                                      const newData = [...editableData];
                                      newData[index] = { ...newData[index], state: e.target.value };
                                      setEditableData(newData);
                                    }}
                                    size="small"
                                    value={row.state || parsed.state || ''}
                                  />
                                ) : (
                                  <Typography variant="body2">
                                    {row.state || parsed.state || 'N/A'}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* ZIP */}
                              <TableCell>
                                {editingRow === index ? (
                                  <TextField
                                    fullWidth
                                    onChange={(e) => {
                                      const newData = [...editableData];
                                      newData[index] = { ...newData[index], zip_code: e.target.value };
                                      setEditableData(newData);
                                    }}
                                    size="small"
                                    value={row.zip_code || parsed.zip || ''}
                                  />
                                ) : (
                                  <Typography variant="body2">
                                    {row.zip_code || parsed.zip || 'N/A'}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* Name */}
                              <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Name: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Name || ''}
                                />
                              ) : (
                                <Typography sx={{ fontWeight: 500 }} variant="body2">
                                  {row.Name || 'No Name'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Email: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Email || ''}
                                />
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  {row.Email || 'No Email'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Phone: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Phone || ''}
                                />
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  {row.Phone || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Phone2: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Phone2 || ''}
                                />
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  {row.Phone2 || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Vehicle: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Vehicle || ''}
                                />
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  {row.Vehicle || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingRow === index ? (
                                <TextField
                                  fullWidth
                                  onChange={(e) => {
                                    const newData = [...editableData];
                                    newData[index] = { ...newData[index], Delinquent: e.target.value };
                                    setEditableData(newData);
                                  }}
                                  size="small"
                                  value={row.Delinquent || ''}
                                />
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  {row.Delinquent || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {editingRow === index ? (
                                <Stack direction="row" justifyContent="center" spacing={1}>
                                  <Button
                                    onClick={() => { handleSaveRow(index, row); }}
                                    size="small"
                                    variant="contained"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    onClick={() => { handleCancelEdit(); }}
                                    size="small"
                                    variant="outlined"
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              ) : (
                                <Stack direction="row" justifyContent="center" spacing={1}>
                                  <Tooltip title="Edit row">
                                    <IconButton
                                      onClick={() => { handleEditRow(index); }}
                                      size="small"
                                    >
                                      <PencilIcon size={16} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove row">
                                    <IconButton
                                      color="error"
                                      onClick={() => { handleRemoveRow(index); }}
                                      size="small"
                                    >
                                      <TrashIcon size={16} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {editableData.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary" variant="body2">
                        No data to display. Please check your CSV file format.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </> : null}
            {/* Import Button */}
            {selectedFile ? <Button
                disabled={isImporting}
                fullWidth
                onClick={handleImport}
                size="large"
                startIcon={<UploadIcon />}
                variant="contained"
              >
                {isImporting ? 'Importing...' : 'Import'}
              </Button> : null}

            {/* Progress Bar */}
            {isImporting ? <Box>
                <Typography sx={{ mb: 1 }} variant="body2">
                  Don't leave the page, this may take a minute... {importProgress}%
                </Typography>
                <LinearProgress value={importProgress} variant="determinate" />
              </Box> : null}

           
          </Stack>
        </CardContent>
      </Card>

     

      {/* Import Results */}
      {importResult ? <Card>
          <CardHeader
            avatar={
              <CheckCircleIcon 
                color={importResult.success ? "var(--mui-palette-success-main)" : "var(--mui-palette-error-main)"} 
                size={24} 
              />
            }
            title="Import Results"
          />
          <CardContent>
            <Stack spacing={2}>
              <Alert severity={importResult.success ? 'success' : 'error'}>
                {importResult.message}
              </Alert>
              
              {importResult.details ? <Box>
                  <Typography sx={{ mb: 2 }} variant="h6">
                    Import Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography color="success.main" variant="h4">
                          {importResult.details.unitsCreated}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          Units Created
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography color="success.main" variant="h4">
                          {importResult.details.usersCreated}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          Users Created
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography color="success.main" variant="h4">
                          {importResult.details.communitiesCreated}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          Communities Created
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {importResult.details.errors && importResult.details.errors.length > 0 ? <Box sx={{ mt: 2 }}>
                      <Typography color="error" sx={{ mb: 1 }} variant="subtitle2">
                        Errors ({importResult.details.errors.length}):
                      </Typography>
                      <Stack spacing={1}>
                        {importResult.details.errors.map((error, index) => (
                          <Typography color="error" key={index} variant="body2">
                            • {error}
                          </Typography>
                        ))}
                      </Stack>
                    </Box> : null}
                </Box> : null}
              
              <Button fullWidth onClick={resetImport} variant="outlined">
                Import Another File
              </Button>
            </Stack>
          </CardContent>
        </Card> : null}
    </Stack>
  );
}